/**
 * i18n core system
 */

import { translations, type AllTranslations } from './translations';

// Supported languages
export type LanguageCode = 'en' | 'fr' | 'de' | 'es';

// Translation context
interface TranslationContext {
  language: LanguageCode;
  translations: Record<keyof AllTranslations, string>;
}

// Global translation context
const translationContext: TranslationContext = {
  language: 'en',
  translations: translations
};

/**
 * Get current language - in-memory only
 */
export function getLanguage(): LanguageCode {
  return translationContext.language;
}

/**
 * Set language - in-memory only
 */
export function setLanguage(language: LanguageCode): void {
  translationContext.language = language;
  // In the future, this would load the appropriate translation file
  // For now, we only support English
}

/**
 * Main translation function
 */
export function t(key: keyof AllTranslations): string;
export function t(key: string): string;
export function t(key: string, values?: Record<string, string | number>): string {
  // Get the translation
  let translation = translationContext.translations[key as keyof AllTranslations] || key;
  
  // Replace placeholders if values are provided
  if (values) {
    Object.entries(values).forEach(([placeholder, value]) => {
      translation = translation.replace(`{${placeholder}}`, String(value));
    });
  }
  
  return translation;
}

/**
 * Get translation (alias for t)
 */
export const getTranslation = t;

/**
 * Initialize i18n system
 */
export function initializeI18n(): void {
  // Set language from localStorage if available
  const language = getLanguage();
  setLanguage(language);
}



// Initialize on module load
if (typeof window !== 'undefined') {
  initializeI18n();
}

// Export types
export type TranslationFunction = typeof t;
export type { AllTranslations as TranslationKeys };

// Helper functions for dynamic messages
export const authMessages = {
  loginToCreate: (entityKey: string): string => {
    const entity = t(entityKey);
    const template = t('auth.login_to_create');
    return template.replace('{entity}', entity);
  },

  loginToView: (entityKey: string): string => {
    const entity = t(entityKey);
    const template = t('auth.login_to_view');
    return template.replace('{entity}', entity);
  },

  loginToAction: (action: string, entityKey: string): string => {
    const entity = t(entityKey);
    const template = t('auth.login_to_manage');
    return template.replace('{entity}', entity);
  },

  loginRequired: (): string => t('auth.login_required'),
  loginToAccess: (): string => t('auth.login_to_access'),
  notAuthenticated: (): string => t('auth.not_authenticated'),
};

export const authToastMessages = {
  loginRequired: () => ({
    title: t('auth.login_required'),
    description: t('auth.login_to_access'),
    variant: 'destructive' as const,
  }),

  loginToCreate: (entityKey: string) => ({
    title: t('auth.login_required'),
    description: authMessages.loginToCreate(entityKey),
    variant: 'destructive' as const,
  }),

  loginToBook: () => ({
    title: t('auth.login_required'),
    description: t('auth.login_to_book_court'),
    variant: 'destructive' as const,
  }),

  authenticationIncomplete: () => ({
    title: t('auth.authentication_incomplete'),
    description: 'Please log in again',
    variant: 'destructive' as const,
  }),
};

export const kpiDescriptions = {
  mrr: (currencyCode: string): string => {
    const template = t('kpi_desc.based_on_currency_prices_monthly');
    return template.replace('Currency', currencyCode);
  },

  changeFromLastMonth: (percentage: number): string => {
    const sign = percentage >= 0 ? '+' : '';
    return `${sign}${percentage}% ${t('change.from_last_month')}`;
  },

  healthStatus: (status: 'Healthy' | 'Warning' | 'Critical'): string => {
    const statusKey = status.toLowerCase() as 'healthy' | 'warning' | 'critical';
    return t(`health.${statusKey}`);
  },
};

// KPI Card Data interface
export interface KPICardData {
  titleKey: string;
  value: string | number;
  descriptionKey?: string;
  icon?: React.ReactNode;
  iconClassName?: string;
  change?: number;
  changeType?: 'positive' | 'negative';
  unit?: string;
  customDescription?: string;
}

 
